import { isWorkflowRunTerminal } from '@/lib/workflows/workflowRunStatus.js';

// How long after a run ends its changes may still be on their way into the governance ledger.
const RECORDING_WINDOW_MS = 2 * 60 * 1000;

/* Where a run's changes stand, so no surface ever shows "0" for changes that have not landed yet.

   A run hands its changes to the governance ledger only once it finishes, and the ledger indexes them seconds later.
   A finished run records how many it handed over (`meta.changeCount`), so `recordedCount` — the rows the ledger
   already holds — falling short of it means one of two things: still recording, if the run just ended; lost in
   ingest, if it ended longer ago than any recording takes. Runs from before `changeCount` existed are taken as
   recorded: whatever the ledger holds for them is all there will ever be.

   Returns `{ state, missingCount }`, `state` one of `loading` (the run itself is not here yet), `running`, `recording`,
   `incomplete`, `recorded`. */
const resolveWorkflowRunChangesState = ({ run, recordedCount = 0, nowMs = Date.now() } = {}) => {
  if (!run) {
    return { state: 'loading', missingCount: 0 };
  }

  if (!isWorkflowRunTerminal(run)) {
    return { state: 'running', missingCount: 0 };
  }

  const expectedCount = run?.meta?.changeCount;

  if (!Number.isInteger(expectedCount) || recordedCount >= expectedCount) {
    return { state: 'recorded', missingCount: 0 };
  }

  const finishedAtMs = Date.parse(run?.finishedAt || '');
  const isRecentlyFinished = Number.isFinite(finishedAtMs) && nowMs - finishedAtMs < RECORDING_WINDOW_MS;

  return { state: isRecentlyFinished ? 'recording' : 'incomplete', missingCount: expectedCount - recordedCount };
};

export default resolveWorkflowRunChangesState;
